import React, { FC, useState, FormEvent, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Redirect } from 'react-router-dom'
import { Button, TextField } from '@material-ui/core'
import Page from 'core/Page'
import { Space, Input, theme, Spinner, Text } from 'ui'
import useStore from 'store'
import { useInput } from 'utils/hooks'
import { Path } from 'core/Routes'
import { useTranslation } from 'react-i18next'
import { Send } from '@material-ui/icons'
import FastInput from 'ui/FastInput'
import vls, { storageKeys } from 'utils/vls'
interface ButtonProps {
  opacity?: number
}

const StyledSend = styled(Send)`
  color:white;
  transform: rotate(180deg);
`
const Form = styled.form`
  width: 28vw;
    @media (max-width: 768px) {
    width: 80vw;
  }
  display: flex;
  flex-direction: column;
`

const StyledPage = styled(Page)`
  background-color:${theme.color.loginBackground};
  height:100%;
  width:100%;
  justify-content:center;
  align-items:center;
`

const StyledTextField = styled(FastInput)`
  background: ${theme.color.mercury};
  border-radius: 5px;
  .MuiInputBase-input::placeholder {
    color: black;
    opacity: 0.5;
    font-size:16px;
  }
  font-size:16px;
  `
const StyledButton = styled(Button) <ButtonProps>`
  height:50px;
  background-color: ${theme.color.cerulean};
  border-radius: 5px;
  opacity:${p => p.opacity ? p.opacity : null};
  color:white;
  :hover {background-color: ${theme.color.cerulean};}
`

const InputContainer = styled.div`
display: flex;
flex-direction: column;
`

const INCORRECT_CODE = (code: string) => `Could not register user,the verificaion code ${code} is wrong, this was try#`
const Failed_CODE = "Could not register user - no verificaion code exists for user with telephpne/email:"

// Escalating cooldown (in minutes) before the user may resend the verification
// code: 2 → 5 → 15 → 30 → 60, capped at 60 for every further resend.
const RESEND_COOLDOWNS_SEC = [2, 5, 15, 30, 60].map(min => min * 60)
const lastCooldownIndex = RESEND_COOLDOWNS_SEC.length - 1

interface ResendState {
  contact: string      // phone or email the cooldown belongs to
  nextAllowedAt: number // epoch ms when resend becomes allowed again
  attemptIndex: number  // index into RESEND_COOLDOWNS_SEC for the *current* wait
}

const formatCountdown = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const secondsUntil = (timestamp: number): number =>
  Math.max(0, Math.ceil((timestamp - Date.now()) / 1000))

const Code: FC = () => {
  const { t } = useTranslation('auth')
  const { user } = useStore()
  const [loginErrorMessage, setLoginErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(0)
  const attemptIndexRef = useRef(0)
  const nextAllowedAtRef = useRef(0)
  const code = useInput({
    placeholder: t('verify_numbers', { numbers: 'XXXXXX' }),
  })

  // The code is sent to email if present, otherwise to phone (see handleSendAgain).
  const contact = user.email !== '' ? user.email : user.phone

  const persistResendState = (attemptIndex: number, nextAllowedAt: number) => {
    attemptIndexRef.current = attemptIndex
    nextAllowedAtRef.current = nextAllowedAt
    const state: ResendState = { contact, nextAllowedAt, attemptIndex }
    vls.set(storageKeys.resendCooldown, state)
  }

  const clearResendState = () => {
    attemptIndexRef.current = 0
    nextAllowedAtRef.current = 0
    setSecondsRemaining(0)
    vls.remove(storageKeys.resendCooldown)
  }

  // On mount: resume an in-progress cooldown (persisted in localStorage so a
  // page reload can't bypass it), or start the initial 2-minute wait for the
  // code that was already sent on the previous (login) screen.
  useEffect(() => {
    if (!contact) return
    const stored: ResendState | false = vls.get(storageKeys.resendCooldown)
    if (stored && stored.contact === contact) {
      attemptIndexRef.current = stored.attemptIndex
      nextAllowedAtRef.current = stored.nextAllowedAt
      setSecondsRemaining(secondsUntil(stored.nextAllowedAt))
    } else {
      const nextAllowedAt = Date.now() + RESEND_COOLDOWNS_SEC[0] * 1000
      persistResendState(0, nextAllowedAt)
      setSecondsRemaining(RESEND_COOLDOWNS_SEC[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact])

  // Tick the countdown once per second, computed from the stored deadline so it
  // stays correct even if the tab was backgrounded.
  useEffect(() => {
    if (secondsRemaining <= 0) return
    const id = setInterval(() => {
      setSecondsRemaining(secondsUntil(nextAllowedAtRef.current))
    }, 1000)
    return () => clearInterval(id)
  }, [secondsRemaining > 0])

  const handleOk = async (event: FormEvent) => {
    event.preventDefault()
    setIsLoading(true)
    if (code.value) {
      let res = await user.sendVerificationCode(code.value)
      if (res.indexOf(Failed_CODE) != -1) {
        res = `${t`codeFailed`} ${user.phone}`
      } else
        if (res.indexOf(INCORRECT_CODE(code.value)) != -1) {
          res = t`incorrectCode`
        }

      if (res) {
        setLoginErrorMessage(res)
      } else {
        // Verified successfully — reset the escalating cooldown.
        clearResendState()
      }
    }
    setIsLoading(false)
  }
  const handleSendAgain = async () => {
    if (secondsRemaining > 0) return // still on cooldown
    if (user.email != '')
      await user.sendPhoneOrEmail(user.email)
    else if (user.phone != '') {
      await user.sendPhoneOrEmail(user.phone)
    }
    // Escalate: 2 → 5 → 15 → 30 → 60 min, capped at the last value.
    const nextIndex = Math.min(attemptIndexRef.current + 1, lastCooldownIndex)
    const nextAllowedAt = Date.now() + RESEND_COOLDOWNS_SEC[nextIndex] * 1000
    persistResendState(nextIndex, nextAllowedAt)
    setSecondsRemaining(RESEND_COOLDOWNS_SEC[nextIndex])
  }

  if (!user.phone && !user.email) return <Redirect to={Path.LOGIN} />
  if (user.hasToken) return <Redirect to={Path.CONFIRM} />

  return (
    <StyledPage flex={true} >
      <Form onSubmit={handleOk}>
        {code.value && code.value != '' &&
          <Text
            size={'sm'}
            textcolor={theme.color.mekomiBlack}
            weight='normal'>
            {t('verify_numbers', { numbers: 'XXXXXX' })}
          </Text>

        }
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {isLoading ? <Spinner /> :
            <>
              <StyledTextField
                autoFocus
                variant='outlined'
                onChange={code.onChange}
              />
              {/* <FastInput onChange={code.onChange} /> */}
            </>
          }
        </div>
        <Space height={1} />
        <Text
          textAlign={'center'}
          opacity={0.6}
          textcolor={loginErrorMessage ? 'red' : theme.color.mekomiBlack}
          weight='normal'>
          {loginErrorMessage ? loginErrorMessage : t`codeInfo`}
        </Text>
        <Space height={2} />
        <StyledButton disabled={isLoading} type="submit" variant='contained' opacity={code.value && code.value != '' ? 1 : 0.3}>
          <Text
            size={'md'}
            textAlign={'center'}
            textcolor={'white'}
            weight='normal'>
            {t`send`}
          </Text>
          <Space width={0.7} />
          <StyledSend />
        </StyledButton>
        <Space height={3} />
        <Text
          textAlign={'center'}
          textcolor={theme.color.codeInfoText}
          weight='normal'>
          {user.phone != '' ? `${t`sentToPhone`} ${user.phone}` : user.email != '' ? `${t`sentToEmail`} ${user.email}` : ''}
        </Text>
        <Space height={3} />
        <Button onClick={handleSendAgain} disabled={secondsRemaining > 0}>
          <Text
            size={'md'}
            textAlign={'center'}
            textcolor={theme.color.sendAgainBlue}
            weight='bold'
          >
            {secondsRemaining > 0
              ? t('resendIn', { time: formatCountdown(secondsRemaining) })
              : t`sendAgain`}
          </Text>
        </Button>
      </Form>
    </StyledPage>
  )

}

export default Code
